import React from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { merge } from 'lodash';
import vars from '$util/theme/vars';
import type { SchemaType } from '$util/schema';
import { Row, Col } from '$src-components/atoms/Grid';
import { CmsImageSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { ContentComponentSection } from '$src-components/atoms/ContentComponentSection';
import { makeContentComponentDefaultConfig } from '$util/makeContentComponentDefaultConfig';
import { makeContentComponentOrderStyles } from '$util/makeContentComponentOrderStyles';
import { Img } from '@/components/atoms/Img';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';

import styles from './index.module.scss';

const UspIndexSchema = z.union([z.literal('1'), z.literal('2'), z.literal('3'), z.literal('4')]);
type UspIndex = z.infer<typeof UspIndexSchema>;

const defaultConfiguration = makeContentComponentDefaultConfig({
  backgroundColor: vars.quantumverse.reskinColors.white,
});

const UspBenefitsSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema.optional(),
    uspIcon: z.record(UspIndexSchema, CmsImageSchema).optional(),
    uspIconTitle: z.record(UspIndexSchema, CmsShortTextSchema).optional(),
    uspHeading: z.record(UspIndexSchema, CmsShortTextSchema),
    uspSubheading: z.record(UspIndexSchema, CmsRichTextSchema).optional(),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
      lazyLoadImages: z.boolean().optional(),
      preloadImages: z.boolean().optional(),
      tabletOrder: z.number().optional(),
      desktopOrder: z.number().optional(),
      widescreenOrder: z.number().optional(),
    })
    .default(defaultConfiguration),
};

export type UspBenefitsTranslatedEntities = SchemaType<(typeof UspBenefitsSchema)['translatedEntities']>;
export type UspBenefitsConfiguration = SchemaType<(typeof UspBenefitsSchema)['configuration']>;

/**
 * A content component that always displays 3 USP benefits.<br />
 * https://www.notion.so/fixter/UspBenefits-cdbe9c6b025e459da69fb215cfe8579f
 */
export const UspBenefits: ContentComponent<UspBenefitsTranslatedEntities, UspBenefitsConfiguration> =
  function UspBenefits({ translatedEntities, configuration, className, ...props }): JSX.Element {
    const { heading, uspIcon, uspIconTitle, uspHeading, uspSubheading } = translatedEntities;
    const {
      backgroundColor = 'white',
      desktopOrder,
      tabletOrder,
      widescreenOrder,
    } = merge(defaultConfiguration, configuration);

    // @NOTE determine how many blocks there are based on either headings, subheadings or icons as this can display any combination
    const numberOfUspItems: number = Math.max(
      (uspIcon && Object.keys(uspIcon).length) || 0,
      Object.keys(uspHeading).length
    );
    const uspItems = Array.from({ length: numberOfUspItems }, (_, i) => `${i + 1}`).map((key) => ({
      key,
      heading: uspHeading[key as UspIndex]?.value,
      subheading: uspSubheading?.[key as UspIndex]?.content,
      icon: uspIcon?.[key as UspIndex],
      iconTitle: uspIconTitle?.[key as UspIndex]?.value,
    }));

    let consolidatedStyles = makeContentComponentOrderStyles({
      desktopOrder,
      tabletOrder,
      widescreenOrder,
    });

    if (consolidatedStyles) {
      consolidatedStyles.backgroundColor = backgroundColor;
    } else {
      consolidatedStyles = { backgroundColor };
    }

    return (
      <ContentComponentSection
        className={classNames(
          UspBenefits.displayName,
          styles.component,
          'ContentComponentsContainer',
          className
        )}
        style={consolidatedStyles}
        {...props}
      >
        <Row className={styles.Row}>
          {heading && (
            <Col extraSmall={4} small={6} medium={3} largeStart={2}>
              <HeadingLevels
                semanticLevel={1}
                styleLevel={3}
                className={classNames(styles.Heading, styles.Override)}
              >
                {heading.value}
              </HeadingLevels>
            </Col>
          )}
          <Col
            className={styles.UspCol}
            extraSmall={4}
            small={6}
            medium={heading ? 9 : 12}
            mediumStart={heading ? 4 : 1}
            large={heading ? 8 : 12}
            largeStart={heading ? 5 : 1}
          >
            {uspItems.map(({ key, icon, iconTitle, heading: uspHeadingValue, subheading }) => {
              const { url: src, alt } = icon || {};
              const uspSubheadingContent = subheading;
              const uspSubheadingValue = uspSubheadingContent && renderRichTextContent(uspSubheadingContent);

              return (
                <div
                  key={key}
                  className={classNames(styles.UspContent, !!iconTitle && styles.HasIconTitle)}
                  style={{ '--items': uspItems.length }}
                >
                  {(src || iconTitle) && (
                    <div className={styles.UspImageContainer}>
                      {src && (
                        <div className={styles.UspImageWrapper}>
                          <Img
                            className={styles.UspImage}
                            src={src}
                            alt={alt || ''}
                            width={64}
                            height={64}
                            objectFit="contain"
                            objectPosition="center"
                            layout="responsive"
                          />
                        </div>
                      )}
                      {iconTitle && <span className={styles.IconTitle}>{iconTitle}</span>}
                    </div>
                  )}
                  <div>
                    <span className={styles.UspHeading}>{uspHeadingValue}</span>
                    {uspSubheadingValue && <div className={styles.UspSubheading}>{uspSubheadingValue}</div>}
                  </div>
                </div>
              );
            })}
          </Col>
        </Row>
      </ContentComponentSection>
    );
  };

UspBenefits.displayName = 'UspBenefits';
UspBenefits.schema = UspBenefitsSchema;
